// src/pages/manager/pchallan/ManagerCreatePChallan.jsx
import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../../../utils/api";
import ManagerLayout from "../../../layouts/ManagerLayout";

const ManagerCreatePChallan = () => {
  const { state } = useLocation();
  const record = state?.production;
  const superAdminId = JSON.parse(localStorage.getItem("user"))?.superAdminId;
  const navigate = useNavigate();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({
    customer: "FACTORY",
    challanNumber: "",
    date: new Date().toISOString().slice(0, 10),
    items: [],
  });

  useEffect(() => {
    async function init() {
      const [pRes, stockRes, cntRes] = await Promise.all([
        api.get(`/api/products/get-all/${superAdminId}`),
        api.get(`/api/reports/closing-stock/${superAdminId}`),
        api.get(`/api/pchallans/count/${superAdminId}`),
      ]);
      const stockMap = {};
      stockRes.data.forEach((s) => (stockMap[s.productName] = s.closingQty));
      const prods = pRes.data.map((p) => ({
        ...p,
        closingQty: stockMap[p.productName] || 0,
        _secFactor: p.secondaryQuantity || 1,
      }));
      setProducts(prods);
      setForm((f) => ({
        ...f,
        challanNumber: String((cntRes.data.count || 0) + 1).padStart(3, "0"),
      }));
      if (record) {
        setForm((f) => ({
          ...f,
          items: record.items.map((it) => {
            const prod = prods.find((p) => p.productName === it.product) || {};
            return {
              product: it.product,
              unit: prod.primaryUnit || "",
              availableQty: prod.closingQty || 0,
              secondaryQuantity: it.secondaryQuantity,
              quantity: it.primaryQuantity,
              price: prod.purchaseRate || 0,
              _secFactor: prod._secFactor || 1,
            };
          }),
        }));
      }
    }
    if (superAdminId) init();
  }, [superAdminId, record]);

  const getTotal = () =>
    form.items.reduce((sum, it) => sum + it.quantity * it.price, 0);

  const handleItemChange = (idx, key, value) => {
    const items = [...form.items];
    const item = { ...items[idx] };

    if (key === "quantity") {
      item.quantity = value;
      item.secondaryQuantity = parseFloat((value / item._secFactor).toFixed(2));
    } else if (key === "secondaryQuantity") {
      item.secondaryQuantity = value;
      item.quantity = parseFloat((value * item._secFactor).toFixed(2));
    } else if (key === "price") {
      item.price = value;
    }

    items[idx] = item;
    setForm({ ...form, items });
  };

  const handleSubmit = async () => {
    try {
      await api.post("/api/pchallans/create", {
        ...form,
        superAdminId,
        total: getTotal(),
      });
      const converted = JSON.parse(localStorage.getItem("converted") || "[]");
      converted.push(record._id);
      localStorage.setItem("converted", JSON.stringify(converted));
      alert("P-Challan created!");
      navigate("/manager/production/view");
    } catch (err) {
      console.error(err);
      alert("Failed to create P-Challan");
    }
  };

  return (
    <ManagerLayout>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Convert to Purchase Challan
        </Typography>

        {/* Header */}
        {isMobile ? (
          <Grid container spacing={2} mb={3}>
            <Grid item xs={12}>
              <TextField
                label="Customer"
                value={form.customer}
                disabled
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Challan No"
                value={form.challanNumber}
                disabled
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                type="date"
                label="Date"
                value={form.date}
                InputLabelProps={{ shrink: true }}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                fullWidth
              />
            </Grid>
          </Grid>
        ) : (
          <Box display="flex" gap={2} mb={3}>
            <TextField
              label="Customer"
              value={form.customer}
              disabled
              fullWidth
            />
            <TextField
              label="Challan No"
              value={form.challanNumber}
              disabled
              fullWidth
            />
            <TextField
              type="date"
              label="Date"
              value={form.date}
              InputLabelProps={{ shrink: true }}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              fullWidth
            />
          </Box>
        )}

        {/* Items */}
        {isMobile ? (
          form.items.map((it, idx) => (
            <Paper key={idx} variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Typography>{it.product}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography>Unit: {it.unit}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography>Stock: {it.availableQty}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Secondary Qty"
                    type="number"
                    size="small"
                    value={it.secondaryQuantity}
                    onChange={(e) =>
                      handleItemChange(
                        idx,
                        "secondaryQuantity",
                        Number(e.target.value)
                      )
                    }
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Primary Qty"
                    type="number"
                    size="small"
                    value={it.quantity}
                    onChange={(e) =>
                      handleItemChange(idx, "quantity", Number(e.target.value))
                    }
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Price"
                    type="number"
                    size="small"
                    value={it.price}
                    onChange={(e) =>
                      handleItemChange(idx, "price", Number(e.target.value))
                    }
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6} container alignItems="center">
                  <Typography>
                    ₹{(it.quantity * it.price).toFixed(2)}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          ))
        ) : (
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ mb: 3, overflowX: "auto" }}
          >
            <Table sx={{ tableLayout: "fixed", width: "100%" }}>
              <TableBody>
                {form.items.map((it, idx) => (
                  <TableRow key={idx}>
                    <TableCell sx={{ width: "16.6%" }}>{it.product}</TableCell>
                    <TableCell sx={{ width: "16.6%" }}>{it.unit}</TableCell>
                    <TableCell sx={{ width: "16.6%" }}>
                      {it.availableQty}
                    </TableCell>
                    <TableCell sx={{ width: "16.6%" }}>
                      <TextField
                        type="number"
                        size="small"
                        value={it.secondaryQuantity}
                        onChange={(e) =>
                          handleItemChange(
                            idx,
                            "secondaryQuantity",
                            Number(e.target.value)
                          )
                        }
                        fullWidth
                      />
                    </TableCell>
                    <TableCell sx={{ width: "16.6%" }}>
                      <TextField
                        type="number"
                        size="small"
                        value={it.quantity}
                        onChange={(e) =>
                          handleItemChange(
                            idx,
                            "quantity",
                            Number(e.target.value)
                          )
                        }
                        fullWidth
                      />
                    </TableCell>
                    <TableCell sx={{ width: "16.6%" }}>
                      <TextField
                        type="number"
                        size="small"
                        value={it.price}
                        onChange={(e) =>
                          handleItemChange(idx, "price", Number(e.target.value))
                        }
                        fullWidth
                      />
                    </TableCell>
                    <TableCell sx={{ width: "16.6%" }}>
                      ₹{(it.quantity * it.price).toFixed(2)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Footer */}
        <Box
          display="flex"
          flexDirection={isMobile ? "column" : "row"}
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Typography variant="h6">Total: ₹{getTotal().toFixed(2)}</Typography>
        </Box>

        <Box textAlign="center">
          <Button
            variant="contained"
            onClick={handleSubmit}
            fullWidth={isMobile}
          >
            Create P‑Challan
          </Button>
        </Box>
      </Container>
    </ManagerLayout>
  );
};

export default ManagerCreatePChallan;
